import { create } from 'zustand';

export type SelectedItem = { id: string; type: 'file' | 'folder' };

interface FileSelectionState {
  selectedItems: Map<string, SelectedItem>;
  lastSelectedIndex: number | null;
}

interface FileSelectionActions {
  toggle: (id: string, type: 'file' | 'folder') => void;
  toggleRange: (
    fromIndex: number,
    toIndex: number,
    items: Array<{ id: string; type: 'file' | 'folder' }>
  ) => void;
  selectAll: (items: Array<{ id: string; type: 'file' | 'folder' }>) => void;
  clearAll: () => void;
  isSelected: (id: string) => boolean;
  getSelectedItems: () => SelectedItem[];
}

export const useFileSelectionStore = create<
  FileSelectionState & FileSelectionActions
>((set, get) => ({
  selectedItems: new Map(),
  lastSelectedIndex: null,

  toggle: (id, type) => {
    set(state => {
      const next = new Map(state.selectedItems);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.set(id, { id, type });
      }
      return { selectedItems: next };
    });
  },

  toggleRange: (fromIndex, toIndex, items) => {
    const start = Math.min(fromIndex, toIndex);
    const end = Math.max(fromIndex, toIndex);

    set(state => {
      const next = new Map(state.selectedItems);
      for (let i = start; i <= end; i++) {
        const item = items[i];
        if (item) {
          next.set(item.id, { id: item.id, type: item.type });
        }
      }
      return { selectedItems: next, lastSelectedIndex: toIndex };
    });
  },

  selectAll: items => {
    const next = new Map<string, SelectedItem>();
    for (const item of items) {
      next.set(item.id, { id: item.id, type: item.type });
    }
    set({ selectedItems: next });
  },

  clearAll: () => {
    set({ selectedItems: new Map(), lastSelectedIndex: null });
  },

  isSelected: id => {
    return get().selectedItems.has(id);
  },

  getSelectedItems: () => {
    return Array.from(get().selectedItems.values());
  },
}));
